import _ from "lodash";
import { CollectionResponse, RemoveResponse } from "../types/ApiResults";
import { BaseServiceImpl } from "./BaseServiceImpl";
import { ManagementService } from "../domain/services/ManagementService";
import {
    IManagement,
    ManagementFlatCollection,
} from "../domain/models/Management";
import { ManagementDTO } from "../domain/models/dto/ManagementDTO";
import { ManagementMapper } from "../mappers/ManagementMapper";

export class ManagementServiceImpl
    extends BaseServiceImpl
    implements ManagementService
{
    static _instance: ManagementService;
    private managementMapper = new ManagementMapper();
    private readonly managementEndpoint = "/api/v1/management";

    private constructor() {
        super();
    }

    static getInstance(): ManagementService {
        if (!ManagementServiceImpl._instance) {
            ManagementServiceImpl._instance = new ManagementServiceImpl();
        }
        return ManagementServiceImpl._instance;
    }

    instantiate(entity: IManagement): IManagement {
        return this.managementMapper.fromDTO(entity);
    }

    async getAll(
        currentPage: number,
        currentLimit: number,
        filtersDTO: any
    ): Promise<CollectionResponse<IManagement>> {
        try {
            let url = `${this.managementEndpoint}?limit=${currentLimit}&page=${currentPage}&getEntityTranslations=true`;
            Object.entries(filtersDTO).forEach(([key, value]) => {
                if (
                    key &&
                    !_.isNil(value) &&
                    (!_.isEmpty(value) || _.isBoolean(value))
                ) {
                    url += `&${key}=${value}`;
                }
            });

            const result = await this.get<ManagementFlatCollection>(url);

            return {
                ...result,
                rows: result.rows.map((dto) =>
                    this.managementMapper.fromDTO(dto)
                ),
            };
        } catch (err) {
            console.log("Error in ManagementServiceImpl.getAll");
            console.log(err);
            throw new Error(String(err));
        }
    }

    async createOrUpdate(dto: IManagement): Promise<IManagement> {
        try {
            const encodedDto = this.managementMapper.toDTO(dto);
            if (dto.id) {
                return this.patch(
                    `${this.managementEndpoint}/${dto.id}`,
                    encodedDto
                );
            }

            return this.post(this.managementEndpoint, encodedDto);
        } catch (err) {
            console.log("Error in ManagementServiceImpl.createOrUpdate");
            console.log(err);
        }
    }

    async deleteById(id: string): Promise<RemoveResponse> {
        try {
            if (!id) return { status: false };
            return super.delete(`${this.managementEndpoint}/${id}`);
        } catch (err) {
            console.log("Error in ManagementServiceImpl.deleteById");
            console.log(err);
        }
    }

    async getById(id: string): Promise<IManagement | null> {
        try {
            if (!id) return null;

            const dto = await this.get<ManagementDTO>(
                `${this.managementEndpoint}/${id}?getEntityTranslations=true`
            );
            return this.managementMapper.fromDTO(dto);
        } catch (err) {
            console.log("Error in ManagementServiceImpl.getById");
            console.log(err);
            return null;
        }
    }
}

export function managementServiceFactory(): ManagementService {
    return ManagementServiceImpl.getInstance();
}
